/**
 * Billing API — fetches Zoho subscription and invoice data.
 *
 * Stored procedures:
 *   getzohosubscription  → subscription info for the campus (may return multiple)
 *   getzohoinvoices      → invoice list for the campus
 */

import { apiFetch } from '../../../services/api.js';
import { getCampusGuid } from '../../../services/session.js';
import { MOCK_SUBSCRIPTIONS, MOCK_INVOICES } from '../data/mockData.js';

/** Set to false when the API endpoints are ready */
const USE_MOCK = false;

/**
 * Fetch all subscriptions for the current campus.
 * @returns {Promise<Array>} Array of subscription objects
 */
export async function fetchSubscriptions() {
  if (USE_MOCK) return MOCK_SUBSCRIPTIONS;
  const rows = await apiFetch({
    reqType: 'stored',
    procedure: 'getzohosubscription',
    pKey: 'campus_id',
    pVal: getCampusGuid(),
  });
  return rows || [];
}

/**
 * Fetch invoices for the current campus.
 * @returns {Promise<Array>} Array of invoice objects
 */
export async function fetchInvoices() {
  if (USE_MOCK) return MOCK_INVOICES;
  return await apiFetch({
    reqType: 'stored',
    procedure: 'getzohoinvoices',
    pKey: 'campus_id',
    pVal: getCampusGuid(),
  });
}

/**
 * Request a Zoho hosted payment page URL for one or more invoices.
 * Backend (Lambda) holds the OAuth refresh token + client secret in
 * AWS Secrets Manager (palcare-zoho-integration) — none of those values
 * are ever sent to the browser.
 *
 * @param {object} args
 * @param {string} args.customerId  — Zoho customer_id
 * @param {Array<{invoice_id: string|number, amount: number}>} args.invoices
 *   — invoices to pay, each with the amount being applied
 * @returns {Promise<{url: string, paymentLinkId: string|null}|null>}
 */
export async function requestZohoPaymentLink({ customerId, invoices }) {
  const rows = await apiFetch({
    reqType: 'stored',
    procedure: 'zohopaymentlink',
    payload: {
      customer_id: customerId,
      invoices,
    },
    readOnly: false,
  });
  const row = rows?.[0];
  if (!row?.url) return null;
  return {
    url: row.url,
    paymentLinkId: row.payment_link_id || null,
  };
}

/**
 * Look up the current status of a previously-generated Zoho payment link.
 *
 * @param {string} paymentLinkId
 * @returns {Promise<{status: string|null}|null>} Zoho link status info, or null on failure.
 *   Common statuses: "generated", "paid", "expired", "cancelled", "voided".
 */
export async function fetchZohoPaymentLinkStatus(paymentLinkId) {
  if (!paymentLinkId) return null;
  const rows = await apiFetch({
    reqType: 'stored',
    procedure: 'zohopaymentlinkstatus',
    payload: { payment_link_id: paymentLinkId },
    readOnly: true,
  });
  return rows?.[0] || null;
}
